import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import firebase from '../firebaseConfig';
import { shareLinkErrorMessage } from '../shareTarget';
import { routeMemberHome } from '../shareRouting';
import PasswordGate from '../components/PasswordGate';
import SharedPlayer from '../components/SharedPlayer';
import OpenInAppBanner from '../components/OpenInAppBanner';
import BrandBar from '../components/BrandBar';
import Login from '../components/Login';
import GradientThumb from '../components/GradientThumb';
import Helpers from '../Helpers';
import { track as trackEvent } from '../analytics';
import icon from '../icon.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faSpinner } from '@fortawesome/free-solid-svg-icons';

/**
 * /t/:slug — global track share link viewer. Mirrors PublicProject but
 * for tracks. Uses `redeemTrackLink`, which bundles the signed audio URL
 * directly in the response. The same CF is re-called to mint a fresh URL
 * when the 5-minute TTL expires.
 *
 * States: loading | error | accountReq | pwGate | ready.
 */
function PublicTrack() {
    const { slug } = useParams();
    const navigate = useNavigate();

    // Members open in the regular project view (mirrors iOS routeToProject).
    const routeIfMember = (result) => routeMemberHome(result, navigate);

    const [status, setStatus] = useState('loading');
    const [errorMessage, setErrorMessage] = useState(null);
    const [pwError, setPwError] = useState(null);
    const [data, setData] = useState(null);
    const [password, setPassword] = useState(null);
    const [albumArtUrl, setAlbumArtUrl] = useState(null);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const playerRef = useRef(null);

    const callRedeem = async (pwd) => {
        const fn = firebase.functions().httpsCallable('redeemTrackLink');
        const res = await fn({ slug, password: pwd || undefined });
        return res.data;
    };

    const renderResult = (result) => {
        setData(result);
        if (result?.track) {
            const meta = result.track.metadata || {};
            const currRevId = meta['current revision'];
            const rev = currRevId && result.track.revisions && result.track.revisions[currRevId];
            setCurrentTrack({
                id: result.trackId,
                name: meta.name || 'Untitled',
                duration: (rev && rev.metadata && rev.metadata.duration) || 0,
            });
        }
        setStatus('ready');
    };

    // Wait for auth to settle so the CF returns a correct isArtistMember.
    useEffect(() => {
        trackEvent('share_link_opened', { slug, kind: 'track' });
        let cancelled = false;
        let fired = false;
        const unsub = firebase.auth().onAuthStateChanged(async () => {
            if (fired || cancelled) return;
            fired = true;
            try {
                const result = await callRedeem(null);
                if (cancelled) return;
                trackEvent('share_link_redeemed', {
                    slug,
                    kind: 'track',
                    permission: result?.permission || 'unknown',
                });
                if (routeIfMember(result)) return;
                renderResult(result);
            } catch (e) {
                if (cancelled) return;
                handleErr(e);
            }
        });
        return () => { cancelled = true; unsub(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const handleErr = (e) => {
        const reason = e?.details?.reason;
        if (reason === 'password-required') { setStatus('pwGate'); return; }
        if (reason === 'account-required')  { setStatus('accountReq'); return; }
        setStatus('error');
        setErrorMessage(shareLinkErrorMessage(e));
    };

    const onPasswordSubmit = async (pwd) => {
        try {
            const result = await callRedeem(pwd);
            setPassword(pwd);
            setPwError(null);
            if (routeIfMember(result)) return;
            renderResult(result);
        } catch (e) {
            const reason = e?.details?.reason;
            if (reason === 'password-required') { setPwError('Wrong password. Try again.'); return; }
            handleErr(e);
        }
    };

    useEffect(() => {
        if (!data) { setAlbumArtUrl(null); return; }
        setAlbumArtUrl(data.albumArtUrl || null);
    }, [data]);

    // The global track redeem bundles the audio URL. Re-call redeem to
    // mint a fresh one when the 5-min signed URL expires.
    const audioFetcher = useMemo(() => {
        return async (_trackId) => {
            const result = await callRedeem(password);
            return { url: result.audioUrl, expiresAt: result.audioExpiresAt };
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, password]);

    const playLogger = useMemo(() => {
        return async (trackId, ms, sessionId) => {
            const fn = firebase.functions().httpsCallable('logSharedTrackPlay');
            await fn({ slug, trackId, ms, sessionId });
        };
    }, [slug]);

    if (status === 'loading') {
        return (
            <div className="splash-screen">
                <img src={icon} alt="Demodeck" className="splash-logo" />
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="shared-error">
                <img src={icon} alt="Demodeck" className="shared-error-logo" />
                <div className="shared-error-message">{errorMessage}</div>
            </div>
        );
    }

    if (status === 'accountReq') {
        const onSignedIn = () => {
            setStatus('loading');
            (async () => {
                try {
                    const result = await callRedeem(password);
                    if (routeIfMember(result)) return;
                    renderResult(result);
                } catch (e) { handleErr(e); }
            })();
        };
        return (
            <div className="shared-account-gate">
                <img src={icon} alt="Demodeck" className="shared-account-gate-logo" />
                <div className="shared-account-gate-title">Sign in to listen</div>
                <div className="shared-account-gate-subtitle">
                    The owner restricted this link to Demodeck users.
                </div>
                <Login onLoginSuccess={onSignedIn} embedded />
            </div>
        );
    }

    if (status === 'pwGate') {
        return <PasswordGate onSubmit={onPasswordSubmit} errorMessage={pwError} />;
    }

    const trackName = data?.track?.metadata?.name || 'Untitled';
    const projectName = data?.projectMeta?.name || '';
    const artistName = data?.artistName || null;
    const trackDuration = currentTrack?.duration || 0;

    return (
        <>
            <BrandBar />
            <div className="shared-page">
                <OpenInAppBanner />
            <div className="shared-page-header">
                {albumArtUrl ? (
                    <img src={albumArtUrl} alt="album art" className="shared-page-art" />
                ) : (
                    <GradientThumb id={data?.trackId} className="shared-page-art" />
                )}
                <div className="shared-page-title-row">
                    <div className="shared-page-meta">
                        <div className="shared-page-title">{trackName}</div>
                        {artistName && (
                            <div className="shared-page-artist">{artistName}</div>
                        )}
                        <div className="shared-page-subtitle">
                            {projectName ? `${projectName} · ` : ''}
                            {Helpers.formatDuration(trackDuration)}
                        </div>
                    </div>
                    <button
                        className="shared-page-play"
                        onClick={() => {
                            if (currentTrack) playerRef.current?.togglePlay();
                        }}
                        disabled={!currentTrack || isLoading}
                        aria-label={isPlaying ? 'Pause track' : 'Play track'}
                    >
                        <FontAwesomeIcon
                            icon={isLoading ? faSpinner : (isPlaying ? faPause : faPlay)}
                            className={isLoading ? 'track-spin' : ''}
                        />
                    </button>
                </div>
            </div>

            <SharedPlayer
                ref={playerRef}
                tracks={currentTrack ? [currentTrack] : []}
                currentTrack={currentTrack}
                setCurrentTrack={setCurrentTrack}
                audioFetcher={audioFetcher}
                playLogger={playLogger}
                projectId={data?.projectId}
                albumArtUrl={albumArtUrl}
                projectName={projectName}
                artistName={artistName}
                onPlayingChange={setIsPlaying}
                onLoadingChange={setIsLoading}
            />
            </div>
        </>
    );
}

export default PublicTrack;
